import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthCacheService, ReplaceSessionCache } from '@pinnakl/auth/providers';
import { EnvironmentService } from '@pinnakl/core/environment';
import { CONFIRMATION_SHARED_DIALOG_CONFIG } from '@pinnakl/shared/constants';
import { AllowedUserTypeAuthParam, ErrorUIToastMessageCategory, UserTypes } from '@pinnakl/shared/types';
import { GeneralConfirmationModalComponent } from '@pinnakl/shared/ui/prime';
import { PinnaklSpinnerService, PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, EMPTY, Observable, of, take, tap } from 'rxjs';
import { ConsentDecision, ConsentDecisionResponse, ConsentDetails } from '../models/consent.model';
import {
  buildSessionConflictContent,
  isSessionConflict,
  SessionConflictResponse,
} from '../models/session-conflict.model';
import {
  SSO_DEFAULT_ERROR_MESSAGE,
  SSO_ERROR_MESSAGES,
  SSO_SESSION_CONFLICT,
  SsoDiscoverResponse,
} from '../models/sso.model';

@Injectable({
  providedIn: 'root',
})
export class AuthenticatorService {
  private readonly router = inject(Router);
  private readonly toast = inject(PinnaklUIToastMessage);
  private readonly spinner = inject(PinnaklSpinnerService);
  private readonly dialogService = inject(DialogService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly http = inject(HttpClient);
  private readonly authCacheService = inject(AuthCacheService);
  private ROUTE_AFTER_LOGIN = '/';
  private version = this.environmentService.version;
  application = 'Desktop';
  baseUrl?: string;
  version$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  errorMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  get authUrls(): Record<
    | 'login'
    | 'linkLogin'
    | 'generateLink'
    | 'verifyotp'
    | 'verifyQrOtp'
    | 'clients'
    | 'selectClient'
    | 'replaceSession'
    | 'consent'
    | 'ssoDiscover',
    string
  > {
    return {
      login: `${this.baseUrl}/account/login`,
      clients: `${this.baseUrl}/account/clients`,
      selectClient: `${this.baseUrl}/account/select-client`,
      linkLogin: `${this.baseUrl}/account/link/login`,
      generateLink: `${this.baseUrl}/account/link`,
      verifyotp: `${this.baseUrl}/account/verifyotp`,
      verifyQrOtp: `${this.baseUrl}/account/totp/verify`,
      replaceSession: `${this.baseUrl}/account/replace-session/request`,
      consent: `${this.baseUrl}/consent`,
      ssoDiscover: `${this.baseUrl}/account/sso/discover`,
    };
  }

  get userType(): UserTypes {
    return this.authCacheService.allowedUserType === AllowedUserTypeAuthParam.EXTERNAL
      ? UserTypes.EXTERNAL
      : UserTypes.INTERNAL;
  }

  get sourceAppURL(): string {
    return this.authCacheService.sourceAppURL;
  }

  get consentReturnUrl(): string | null {
    return new URLSearchParams(this.authCacheService.lastURLSearch).get('returnUrl');
  }

  constructor() {
    this.baseUrl = this.environmentService.get('authUrl');
    this.errorMessage$.next(this.authCacheService.errorMessage);
    this.authCacheService.clearErrorMessage();
  }

  get ssoDiscoveryAvailable(): boolean {
    return this.userType === UserTypes.INTERNAL && !!this.consentReturnUrl;
  }

  continueWithEmail(email: string, onPassword: () => void): void {
    this.spinner.spin('auth');
    this.http
      .post<SsoDiscoverResponse>(
        this.authUrls.ssoDiscover,
        { email, returnUrl: this.consentReturnUrl },
        { withCredentials: true },
      )
      .pipe(catchError(() => of(null)))
      .subscribe(sso => {
        if (sso && !sso.requiresPassword && sso.ssoUrl) {
          window.location.assign(sso.ssoUrl);
          return;
        }
        this.spinner.stop('auth');
        onPassword();
      });
  }

  processSsoError(): void {
    const error = this.authCacheService.ssoError;
    if (!error || window.location.search) {
      return;
    }
    this.authCacheService.clearSsoError();
    const lastActivityAt = error.lastActivityAt ?? '';
    if (error.code === SSO_SESSION_CONFLICT && error.replaceToken && !Number.isNaN(Date.parse(lastActivityAt))) {
      this.handleSessionConflict(
        {
          error: SSO_SESSION_CONFLICT,
          message: '',
          replaceToken: error.replaceToken,
          session: {
            city: error.city ?? null,
            country: error.country ?? null,
            lastActivityAt,
          },
        },
        this.authCacheService.lastURLSearch,
      );
      return;
    }
    this.errorMessage$.next(SSO_ERROR_MESSAGES[error.code] ?? SSO_DEFAULT_ERROR_MESSAGE);
  }

  // The server sends cookies in Set-Cookie header. Without this, Angular will ignore the Set-Cookie header
  login(login: { username: string; password: string }): void {
    this.spinner.spin('auth');
    this.authCacheService.clearReplaceSession();
    const user = {
      ...login,
      application: this.application,
      userType: this.userType,
    };
    const loginUrl = `${this.baseUrl}/account/login` + this.authCacheService.lastURLSearch;
    this.http.post<{ returnUrl: string }>(loginUrl, user, { withCredentials: true }).subscribe({
      next: (res: { returnUrl: string } | null) => {
        if (res.returnUrl.includes('/2fa')) {
          this.authCacheService.credentials = login;
        }
        this.redirectToSource(res.returnUrl);
      },
      error: e => {
        if (isSessionConflict(e)) {
          this.handleSessionConflict(e.error, this.authCacheService.lastURLSearch);
          return;
        }
        if (e?.status === 401) {
          this.errorMessage$.next('Email/Password combination is incorrect!');
        } else {
          this.errorMessage$.next('Authentication has been reset. Please login.');
          this.redirectToSource();
        }
        this.spinner.stop('auth');
      },
    });
  }

  // Ends the other device's session after the step-up code is verified; the response is the same 2FA url as a login
  replaceSession(replace: ReplaceSessionCache): void {
    this.spinner.spin('auth');
    this.http
      .post<{ returnUrl: string }>(
        `${this.authUrls.replaceSession}${replace.search}`,
        { replaceToken: replace.token },
        { withCredentials: true },
      )
      .subscribe({
        next: ({ returnUrl }) => {
          this.authCacheService.replaceSession = replace;
          this.redirectToSource(returnUrl);
        },
        error: e => {
          this.spinner.stop('auth');
          this.authCacheService.clearReplaceSession();
          if (e?.status === 400 || e?.status === 401) {
            this.errorMessage$.next('This session expired, please sign in again.');
            this.router.navigateByUrl('/login');
          } else {
            this.errorMessage$.next('Authentication has been reset. Please login.');
            this.redirectToSource();
          }
        },
      });
  }

  verifyOtp(otp: string): void {
    this.spinner.spin('auth');
    const params = new URLSearchParams(this.authCacheService.lastURLSearch);
    const token = params.get('token');
    const returnUrlPath = params.get('returnUrl');
    const returnUrl = encodeURIComponent(`${this.baseUrl}` + returnUrlPath);
    const verifyUrl = `${this.authUrls.verifyotp}?token=${token}&otp=${otp}&returnUrl=${returnUrl}`;
    this.http.get<{ returnUrl: string }>(verifyUrl, { withCredentials: true }).subscribe({
      next: ({ returnUrl }) => {
        this.spinner.stop('auth');
        this.authCacheService.clearCredentials();
        this.redirectToSource(returnUrl);
      },
      error: () => {
        this.spinner.stop('auth');
        this.toast.error(
          'Incorrect OTP submitted. Please click "Resend code" to generate a new OTP',
          ErrorUIToastMessageCategory.Error,
          { sticky: true },
        );
      },
    });
  }

  verifyQrOtp(otp: string): void {
    this.spinner.spin('auth');
    const params = new URLSearchParams(this.authCacheService.lastURLSearch);
    const token = params.get('token');
    const returnUrlPath = params.get('returnUrl');
    const returnUrl = encodeURIComponent(`${this.baseUrl}` + returnUrlPath);
    const verifyUrl = `${this.authUrls.verifyQrOtp}?token=${token}&otp=${otp}&returnUrl=${returnUrl}`;
    this.http.get<{ returnUrl: string }>(verifyUrl, { withCredentials: true }).subscribe({
      next: ({ returnUrl }) => {
        this.spinner.stop('auth');
        this.authCacheService.clearCredentials();
        this.redirectToSource(returnUrl);
      },
      error: () => {
        this.spinner.stop('auth');
        this.toast.error('Incorrect OTP submitted', ErrorUIToastMessageCategory.Error, {
          sticky: true,
        });
      },
    });
  }

  regenerateMagicLink(email, callback?: () => void) {
    this.spinner.spin('auth');
    const endpoint = `${this.authUrls.generateLink}`;
    const returnUrl = `${this.sourceAppURL}${this.ROUTE_AFTER_LOGIN}`;
    return this.http
      .post<any>(endpoint, { email, usertype: this.userType, returnUrl }, { withCredentials: true })
      .subscribe({
        next: () => {
          this.spinner.stop('auth');
          this.toast.success(
            `A new authentication link has been sent to ${email}. Click on the link to create new password.`,
          );
          callback?.();
        },
        error: () => {
          this.spinner.stop('auth');
          this.toast.error('Error while regenerating magic link', ErrorUIToastMessageCategory.Error, {
            sticky: true,
          });
        },
      });
  }

  magicLinkNavigated(): Observable<void> {
    const isLatestAppVersion = this.isLatestAppVersion();
    const search = new URLSearchParams(this.authCacheService.lastURLSearch);
    const token = search.get('token');
    const returnUrl = search.get('returnUrl');
    const otpChannel = search.get('otpChannel');

    if (!isLatestAppVersion) {
      this.setCurrentAppVersion();
      window.location.assign(window.location.href);
      return of();
    }

    return this.postAccountLinkLogin(token).pipe(
      tap(response => {
        this.redirectToSource(otpChannel === 'qr' || otpChannel === 'mobile' ? response.returnUrl : returnUrl);
      }),
      catchError(error => {
        if (isSessionConflict(error)) {
          this.handleSessionConflict(error.error, '', () => this.redirectToSource());
          return EMPTY;
        }
        if (error.error?.passwordResetRequired) {
          this.router.navigateByUrl('/reset-password?isLinkExpired=true');
        }
        throw error;
      }),
    );
  }

  selectClientLinkNavigated(): Observable<{ id: number; name: string }[] | null> {
    const isLatestAppVersion = this.isLatestAppVersion();

    if (!isLatestAppVersion) {
      this.setCurrentAppVersion();
      window.location.assign(window.location.href);
      return of(null);
    }

    return this.getClientsToSelect();
  }

  redirectToSource(redirectUri?: string): void {
    const redirectToUrl = redirectUri ?? this.sourceAppURL;
    redirectToUrl ? window.location.assign(redirectToUrl) : this.router.navigateByUrl('/login');
  }

  redirectToLogin(redirectUri?: string): void {
    window.location.assign(redirectUri ?? this.sourceAppURL);
  }

  selectClient(clientId: number): Observable<{ returnUrl: string }> {
    const search = new URLSearchParams(this.authCacheService.lastURLSearch);
    const returnUrl = search.get('returnUrl');
    return this.http.post<{ returnUrl: string }>(
      `${this.authUrls.selectClient}?returnUrl=${encodeURIComponent(returnUrl)}`,
      {
        clientId,
      },
      { withCredentials: true },
    );
  }

  getConsentDetails(returnUrl: string): Observable<ConsentDetails> {
    return this.http.get<ConsentDetails>(`${this.authUrls.consent}?returnUrl=${encodeURIComponent(returnUrl)}`, {
      withCredentials: true,
    });
  }

  submitConsentDecision(returnUrl: string, decision: ConsentDecision): Observable<ConsentDecisionResponse> {
    return this.http.post<ConsentDecisionResponse>(
      this.authUrls.consent,
      { returnUrl, decision },
      { withCredentials: true },
    );
  }

  // `search` is the query string of the login call that hit the conflict, so the replace request keeps its returnUrl
  private handleSessionConflict(conflict: SessionConflictResponse, search: string, onCancel?: () => void): void {
    this.spinner.stop('auth');
    const dialogRef = this.dialogService.open(GeneralConfirmationModalComponent, {
      ...CONFIRMATION_SHARED_DIALOG_CONFIG,
      header: 'Session already active',
      data: {
        confirmButtonLabel: 'Continue',
        cancelButtonLabel: 'Cancel',
        content: buildSessionConflictContent(conflict.session),
      },
    });
    dialogRef?.onClose.pipe(take(1)).subscribe(confirmed => {
      if (confirmed) {
        this.replaceSession({ token: conflict.replaceToken, search });
      } else {
        onCancel?.();
      }
    });
  }

  private postAccountLinkLogin(token: string, otp?: string): Observable<any> {
    const url = this.authUrls.linkLogin;
    const body = {
      token,
      ...(otp ? { otp } : {}),
    };
    return this.http.post(url, body, { withCredentials: true });
  }

  private getClientsToSelect(): Observable<{ id: number; name: string }[]> {
    const url = this.authUrls.clients;
    return this.http.get<{ id: number; name: string }[]>(url, { withCredentials: true });
  }

  private setCurrentAppVersion(): void {
    localStorage.setItem('appVersion', this.version);
  }

  private isLatestAppVersion(): boolean {
    const appVersion = localStorage.getItem('appVersion');
    if (!appVersion) return false;
    return this.version === appVersion;
  }
}
